import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import { Typography, styled } from '@mui/material';
import { alpha } from '@mui/system';
import { Editor } from '@tiptap/react';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import {
  ConversationMessageContentType,
  IConversationMessage,
} from 'src/@types/conversation';
import { CPAvatar } from 'src/components/base/CPAvatar';
import { useWorkspaceUser } from 'src/hooks/useWorkspace';
import { useLocales } from 'src/locales';
import { useConversationContext } from 'src/provider/ConversationContext';
import { ConversationMessageContent } from '../ConversationMessageContent';

type Props = {
  message: IConversationMessage;
  editor: Editor;
};

export function ConversationMessageReply({ message, editor }: Props) {
  const canExpand = useMemo(
    () =>
      (message.content?.length || 0) > 100 &&
      message.contentType === ConversationMessageContentType.TEXT,
    [message.content?.length, message.contentType],
  );

  const [expanded, setExpanded] = useState(false);
  const { translate } = useLocales();

  const author = useWorkspaceUser(message.author);

  const { goToMessage, setGoToMessage } = useConversationContext();

  const handleGoToMessage = () => {
    if (message._id === goToMessage) {
      setGoToMessage(undefined);
    }
    setTimeout(() => setGoToMessage(message._id));
  };

  return (
    <StyledContainer>
      <Connector />
      <StyledWrapper
        className={cn(message.contentType, { expanded, canExpand })}
        onClick={handleGoToMessage}
      >
        {!!author && (
          <UserInfo>
            <UserProfileShortPopoverTrigger userId={author._id}>
              <CPAvatar
                picture={author.picture}
                name={author.name}
                size="xSmall"
              />
            </UserProfileShortPopoverTrigger>
            <Username variant="subtitle2">{author.name}</Username>
          </UserInfo>
        )}

        <StyledReplyContent
          className={cn({
            isText: message.contentType === ConversationMessageContentType.TEXT,
          })}
        >
          <ConversationMessageContent
            message={message}
            compact
            className={cn({ expanded, replyContent: true })}
            editor={editor}
            isReply
          />
          {canExpand && !expanded && (
            <Typography variant="caption" color="text.secondary">
              ...
            </Typography>
          )}
          {canExpand && (
            <ExpandButton
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              {expanded
                ? translate('shared.buttons.show_less')
                : translate('shared.buttons.view')}
            </ExpandButton>
          )}
        </StyledReplyContent>
      </StyledWrapper>
    </StyledContainer>
  );
}

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  padding: theme.spacing(0.5),
  gap: theme.spacing(1),
}));

const Connector = styled('div')(({ theme }) => ({
  width: 20,
  height: 30,
  borderTopLeftRadius: theme.spacing(1),
  borderLeft: `solid 1px ${theme.palette.divider}`,
  borderTop: `solid 1px ${theme.palette.divider}`,
}));

const ExpandButton = styled('span')(({ theme }) => ({
  cursor: 'pointer',
  color: theme.palette.secondary.light,
  fontSize: theme.typography.caption.fontSize,
  marginLeft: theme.spacing(1),
  whiteSpace: 'nowrap',

  '&:hover': {
    textDecoration: 'underline',
  },
}));

const UserInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Username = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  whiteSpace: 'nowrap',
  color: theme.palette.text.secondary,
}));

const StyledWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flexWrap: 'wrap',

  gap: theme.spacing(0.75),
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.grey[500], 0.05),
  border: `solid 1px ${alpha(theme.palette.grey[500], 0.08)}`,
  cursor: 'pointer',

  '&.expanded': {
    flexGrow: 1,
  },
  '&:not(.expanded)&:has(.canExpand)': {
    minWidth: 'clamp(120px, 320px, 100%)',
  },
}));

const StyledReplyContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  '&.isText': {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  '& .replyContent': {
    fontSize: '.75rem',
    '&:not(.expanded)': {
      maxWidth: '50ch',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'inline-block',
      width: 'auto',
    },
  },
}));
